import { AnalyticsBrowser, Context, SegmentEvent } from '@segment/analytics-next'
import { MiddlewareParams } from '@segment/analytics-next/dist/types/plugins/middleware'
import isNil from 'lodash/isNil'
import { SYNTHETIC_ORG_ID } from '@omnea-app/packages/universal/e2e-test-constants/syntheticOrg'

export type Track = (event: string | SegmentEvent, properties?: any) => Promise<Context>
type Page = (event: string, properties?: any) => Promise<Context>
type Identify = (userId: string, properties?: any) => Promise<Context>
type Group = (orgId: string, properties?: any) => Promise<Context>
type Analytics = {
  track: Track
  page: Page
  identify: Identify
  group: Group
}

const getStubbedAnalytics = async (): Promise<Analytics> => {
  const track: Track = (event, properties) => {
    console.debug('stubbed analytics TRACK:', event, properties)
    return {} as Promise<Context>
  }
  const page: Page = (name, properties) => {
    console.debug('stubbed analytics PAGE:', name, properties)
    return {} as Promise<Context>
  }
  const identify: Identify = (userId, properties) => {
    console.debug('stubbed analytics IDENTIFY:', userId, properties)
    return {} as Promise<Context>
  }
  const group: Group = (orgId, properties) => {
    console.debug('stubbed analytics GROUP:', orgId, properties)
    return {} as Promise<Context>
  }

  return { track, page, identify, group }
}

const dropPingdomEvents = ({ payload, next }: MiddlewareParams) => {
  if (payload.userAgent() === 'Pingdom') {
    return
  }
  next(payload)
}

// The synthetic verification org runs a browser monitor against production; drop all of its
// events at the source so they never reach Segment. Only identify/group carry the org id, so the
// decision is updated only when it is present — a later org-less identify (e.g. flag enrichment)
// leaves it intact. track/page carry no org and follow the last resolved decision.
const dropSyntheticOrgEvents = () => {
  let isSyntheticOrg = false
  return ({ payload, next }: MiddlewareParams) => {
    try {
      const { type, groupId, traits } = payload.obj
      if (type === 'group' && groupId != null) {
        isSyntheticOrg = groupId === SYNTHETIC_ORG_ID
      } else if (type === 'identify' && traits?.organisationId != null) {
        isSyntheticOrg = traits.organisationId === SYNTHETIC_ORG_ID
      }
    } catch {
      // A malformed payload must never break the analytics pipeline or affect other orgs: leave
      // the last decision untouched and fall through to sending this event.
    }
    if (isSyntheticOrg) {
      return
    }
    next(payload)
  }
}

type GetSegmentAnalyticsOptions = {
  segmentWriteKey: string
  cdnUrl?: string
  apiHost?: string
}

const getSegmentAnalytics = async ({ segmentWriteKey, cdnUrl, apiHost }: GetSegmentAnalyticsOptions): Promise<Analytics> => {
  // https://www.twilio.com/docs/segment/connections/sources/catalog/libraries/website/javascript/custom-proxy#npm-instructions
  const analytics = AnalyticsBrowser.load(
    {
      writeKey: segmentWriteKey,
      cdnURL: cdnUrl,
    },
    isNil(apiHost)
      ? undefined
      : {
          integrations: {
            'Segment.io': {
              // POST https://MY-CUSTOM-API-PROXY.com/v1/t --> proxies to
              // https://api.segment.io/v1/t
              apiHost: `${apiHost}/v1`,
              protocol: 'https',
            },
          },
        }
  )

  await analytics.addSourceMiddleware(dropPingdomEvents)
  await analytics.addSourceMiddleware(dropSyntheticOrgEvents())

  return {
    track: analytics.track,
    page: analytics.page,
    identify: analytics.identify,
    group: analytics.group,
  }
}

export const getAnalyticsClient = async (options: GetSegmentAnalyticsOptions): Promise<Analytics> => {
  try {
    // only run in browser
    return typeof window !== 'undefined' && process.env.OMNEA_APP_ENV === 'production'
      ? getSegmentAnalytics(options)
      : getStubbedAnalytics()
  } catch (err) {
    console.error('Error loading analytics client', err)
    return await getStubbedAnalytics()
  }
}

export const analytics = getAnalyticsClient({
  segmentWriteKey: process.env.SEGMENT_WRITE_KEY || '',
  cdnUrl: process.env.SEGMENT_CDN_URL,
  apiHost: process.env.SEGMENT_API_HOST,
})
